import { Box, Stack } from '@mantine/core';
import { Text, Title } from '@repo/foundations';
import { memo, useCallback, useState } from 'react';

import { notifications } from '@mantine/notifications';
import { SettingsTextInput } from '@repo/common/components';
import { useDebounceFn } from 'ahooks';
import { useUpdateIntegration, type IIntegration } from '../../../../../api';

export const GithubPreferences = memo(
	({ integration }: { integration: IIntegration }) => {
		const { mutateAsync: updateIntegration } = useUpdateIntegration({});

		const handleWidthChange = useCallback(
			(value: string) => {
				const number = Number(value);
				if (!isNaN(number) && Number.isInteger(number)) {
					updateIntegration({
						data: { id: integration.id, credentials: { width: value } },
					});
					notifications.show({
						title: 'Successfully updated width',
						message: 'Width has been updated',
						color: 'green',
					});
				} else {
					notifications.show({
						title: 'Invalid input for width',
						message: 'Width must be a positive integer',
						color: 'red',
					});
				}
			},
			[updateIntegration, integration]
		);

		const initialWidth = (integration.credentials?.width as string) || '80';
		const [width, setWidth] = useState<string>(initialWidth);
		const { run: debouncedHandleWidthChange } = useDebounceFn(
			handleWidthChange,
			{ wait: 350 }
		);

		return (
			<Stack spacing="md">
				<Stack spacing={0}>
					<Title size="md">YAML width</Title>
					<Text size="sm" color="text/secondary/default">
						The maximum width of YAML file used to style the code. Default width
						is 80 characters
					</Text>
				</Stack>
				<Box>
					<SettingsTextInput
						title="YAML width"
						description="The maximum width of YAML file used to style the code. Default width is 80 characters"
						value={width}
						placeholder="80"
						onChange={(value) => {
							setWidth(value);
							debouncedHandleWidthChange(value);
						}}
					/>
				</Box>
			</Stack>
		);
	}
);
